import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A polar area chart, also known as a radial bar chart or a Coxcomb chart, is a circular
          chart that displays data in segments along a radial axis. Each segment has a fixed angular
          width but varying radius, representing the value of a particular category. This chart is
          useful for comparing the size of items or showing cyclical patterns, such as variations in
          seasonal data, time-based trends, or other periodic data. Polar area charts are ideal when
          you want to visualize the relationship between data points across different categories in
          a visually striking and radial format.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Polar area charts are useful for visualizing cyclical or directional data in a radial
          format. Here are three key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Seasonal or Cyclical Data:</strong> Polar area charts are ideal for
            showing patterns that repeat over time, such as monthly sales data, temperature
            variations by month, or daily traffic patterns.
          </li>
          <li>
            <strong>Comparing Data Across Categories:</strong> By representing data points as
            segments of a circle, polar area charts allow easy comparison of values across different
            categories, making them great for visualizing variations in metrics like performance
            across regions or product categories.
          </li>
          <li>
            <strong>Visualizing Proportions with a Radial Twist:</strong> These charts provide a
            unique way to represent part-to-whole relationships, similar to pie charts, but with
            more emphasis on the variation in data values, offering a fresh perspective on
            proportional comparisons.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Polar area charts provide a visually appealing way to display data, but they come with
          notable limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Area Perception:</strong> Human perception struggles with
            accurately comparing segment areas, leading to potential misinterpretation of the data.
          </li>
          <li>
            <strong>Limited to Single-Dimensional Data:</strong> Polar area charts can only
            represent one dimension of data, making them unsuitable for datasets with multiple
            variables or intricate relationships.
          </li>
          <li>
            <strong>Clutter with Many Segments:</strong> Charts with numerous segments become
            visually crowded, reducing clarity and making it harder to identify patterns or trends.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection plays a critical role in polar area charts, enhancing visual clarity and
          data interpretation. Distinct colors for each segment help differentiate categories and
          ensure readability, especially when multiple data points overlap. Gradual color gradients
          effectively represent variations in magnitude, highlighting differences across segments.
          Using a cohesive color palette can create a harmonious visual impact while maintaining
          focus on the data&apos;s most significant aspects. Proper color choices ensure the chart
          remains engaging and easy to understand.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Polar area charts are a unique way to display proportional data in a circular layout, but
          they come with distinct challenges. Here are three significant ones:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Value Interpretation:</strong> Differences in segment areas can be
            hard to assess accurately, as viewers must compare angular and radial dimensions
            simultaneously.
          </li>
          <li>
            <strong>Limited Space for Categories:</strong> With many data points, the chart becomes
            cluttered, making it difficult to distinguish between segments or read labels.
          </li>
          <li>
            <strong>Misleading Visual Impact:</strong> Larger radii exaggerate the perception of
            differences, as area grows exponentially, potentially distorting the importance of
            smaller categories.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
